import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

export const useAdminCollection = (resource, label) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/${resource}`);
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [resource]);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setBusy(true);
    try {
      if (editing.id) await api.patch(`/admin/${resource}/${editing.id}`, payload);
      else await api.post(`/admin/${resource}`, payload);
      toast.success(editing.id ? `${label} updated.` : `${label} added.`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/admin/${resource}/${item.id}`);
      toast.success(`"${item.name}" removed.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (item) => {
    const { id, order, ...rest } = item;
    try {
      await api.patch(`/admin/${resource}/${id}`, { ...rest, active: !item.active });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return { items, editing, setEditing, busy, save, remove, toggleActive };
};

export const CardActions = ({ item, prefix, onEdit, onDelete }) => (
  <>
    <button
      data-testid={`${prefix}-edit-${item.id.slice(0, 8)}`}
      onClick={() => onEdit(item)}
      className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-1.5 text-xs font-bold text-parchment transition-colors hover:bg-spice"
    >
      <Pencil size={12} /> Edit
    </button>
    <button
      data-testid={`${prefix}-delete-${item.id.slice(0, 8)}`}
      onClick={() => onDelete(item)}
      className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-spice/40 px-3.5 py-1.5 text-xs font-bold text-spice transition-colors hover:bg-spice hover:text-parchment"
    >
      <Trash2 size={12} /> Delete
    </button>
  </>
);

export const EditorHeader = ({ summary, addLabel, testid, onAdd }) => (
  <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
    <p className="text-sm text-muted2">{summary}</p>
    <button data-testid={testid} onClick={onAdd} className="inline-flex items-center gap-2 rounded-full bg-spice px-5 py-2.5 text-sm font-bold text-parchment transition-colors hover:bg-ink">
      <Plus size={15} /> {addLabel}
    </button>
  </div>
);
